import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { getUserMe } from '../../../apps/main/rootReducer';
import { apiFetchSettings } from '../../settings/api';
import { ESettingScope, SettingKey } from '../../../models/Settings';

export const ABSENCE_CUSTOM_NOTE_SETTING_KEY = 'Absence_CustomNote_Setting';

export interface AbsenceNote {
  internalNote: string;
  externalNote: string;
}

const useIndividualAbsenceNote = () => {
  const userMe = useSelector(getUserMe);

  const keys: SettingKey[] = useMemo(
    () => [
      {
        keys: [ABSENCE_CUSTOM_NOTE_SETTING_KEY],
        scopeId: userMe?.id || '',
        eSettingScope: ESettingScope.USER,
      } as SettingKey,
    ],
    [userMe?.id]
  );

  const { data: fetchedSettings, isLoading } = useQuery({
    queryKey: [ESettingScope.USER, userMe?.id, 'setting', ABSENCE_CUSTOM_NOTE_SETTING_KEY],
    queryFn: () => apiFetchSettings(keys),
    staleTime: 1000 * 60 * 5,
    enabled: !!userMe?.id,
  });

  const { absenceNote, defaultAbsenceNote, settingId } = useMemo(() => {
    const emptyNote: AbsenceNote = { internalNote: '', externalNote: '' };

    if (!fetchedSettings?.data) {
      return {
        absenceNote: emptyNote,
        defaultAbsenceNote: emptyNote,
        settingId: null as string | null,
      };
    }

    const arr = Array.isArray(fetchedSettings.data)
      ? (fetchedSettings.data as any[])
      : [fetchedSettings.data];

    const parseNote = (record: any): AbsenceNote => {
      try {
        const parsed = JSON.parse(record.jsonValue || '{}');
        return {
          internalNote: parsed.internalNote || '',
          externalNote: parsed.externalNote || '',
        };
      } catch {
        return emptyNote;
      }
    };

    const scopePriority = [
      ESettingScope.OFFICE,
      ESettingScope.GLOBAL,
      ESettingScope.NONE,
    ];

    const userRecord = arr.find((s) => s.scope === ESettingScope.USER);
    const fallbackRecord = scopePriority
      .map((scope) => arr.find((s) => s.scope === scope))
      .find(Boolean);
    const defaultNote = fallbackRecord ? parseNote(fallbackRecord) : emptyNote;

    if (!userRecord) {
      return {
        absenceNote: emptyNote,
        defaultAbsenceNote: defaultNote,
        settingId: null as string | null,
      };
    }

    return {
      absenceNote: parseNote(userRecord),
      defaultAbsenceNote: defaultNote,
      settingId: userRecord.settingId as string,
    };
  }, [fetchedSettings]);

  const hasAbsenceNote =
    absenceNote.internalNote.trim().length > 0 ||
    absenceNote.externalNote.trim().length > 0;

  return {
    hasAbsenceNote,
    absenceNote,
    defaultAbsenceNote,
    isLoading,
    settingId,
  };
};

export default useIndividualAbsenceNote;
